// Shared testing-mode notice + password reveal/entry, used by:
//   - TestingModeGate.tsx (full-screen blocker for app-surface routes)
//   - SplashScreen.tsx    (in-place replacement for the CTA buttons,
//                          rendered when the public splash is shown
//                          while VITE_TESTING_MODE=1 is active)
//
// Extracted so both consumers render the same badge + message +
// "I have a password" reveal copy, and the verify→localStorage
// →onUnlocked flow lives in one place. Removed alongside the rest
// of the testing-mode machinery at public launch.
//
// Bilingual inline strings — public-launch placeholder copy. The
// component renders on the public surface that may be visible
// before LangProvider settles (TestingModeGate sits above i18n),
// so we hardcode KR + EN rather than routing through useT().
import { useEffect, useRef, useState } from 'react'

import s from './TestingPasswordPanel.module.css'

// Same key SiteGate / TestingModeGate use. Storing under the shared
// key means a successful bypass in this panel auto-unlocks the
// inner SiteGate without a second prompt.
const SITE_PASSWORD_KEY = 'siteAccess'

export const TESTING_PANEL_COPY = {
  badge: '테스트 중 / In Testing',
  message:
    '비공개 테스트 중입니다. 곧 공개됩니다.\nPrivate testing, public launch coming soon.',
  haveCode: '비밀번호가 있어요 / I have a password',
  passwordPlaceholder: '비밀번호 / Password',
  submit: '입장 / Enter',
  checking: '확인 중… / Checking…',
  wrong: '비밀번호가 올바르지 않습니다 / Wrong password',
}

/**
 * Send the password as the ``X-Site-Password`` header on a GET to
 * ``/api/probe``. Only 204 (the middleware's explicit "password
 * matched" response) unlocks; everything else fails closed.
 *
 * Fail-closed posture is deliberate for this entry point: a 5xx /
 * 404 / 502 / etc. means we can't actually verify the password, so
 * we shouldn't grant access. SiteGate's mount-time re-validation
 * has different semantics (preserves an already-valid stored
 * password through transient backend hiccups) and lives separately.
 *
 * CodeRabbit on PR #241 — original lenient ``status !== 401``
 * shape would have unlocked on a backend outage, defeating the
 * gate during exactly the moments operators most need it intact.
 */
async function verifyPassword(pw: string): Promise<boolean> {
  try {
    const res = await fetch('/api/probe', {
      method: 'GET',
      headers: { 'X-Site-Password': pw },
    })
    return res.status === 204
  } catch {
    return false
  }
}

interface TestingPasswordPanelProps {
  /** Fired when a password successfully validates and lands in
   *  ``localStorage[siteAccess]``. The parent typically flips its
   *  ``bypassed`` state so the testing notice unmounts and the real
   *  content takes over. */
  onUnlocked: () => void
  /** When true, hides the eehae logo + badge + message and renders
   *  only the inline reveal/form. Used by SplashScreen where the
   *  splash itself already provides logo + brand chrome — repeating
   *  it inside the buttons area would double the brand. */
  compact?: boolean
}

/**
 * The testing-mode notice + reveal-on-click password form.
 *
 * In its default (non-compact) form: badge, bilingual notice,
 * "I have a password" reveal that expands into a small inline form.
 * In compact form: just the message text + reveal/form (no badge).
 */
export default function TestingPasswordPanel({
  onUnlocked,
  compact = false,
}: TestingPasswordPanelProps) {
  const [revealed, setRevealed] = useState(false)
  const [value, setValue] = useState('')
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Auto-focus the password field when the reveal opens. Small
  // delay so focus lands after the fade-in transition starts;
  // otherwise the input flashes into view already focused.
  useEffect(() => {
    if (!revealed) return
    const id = window.setTimeout(() => {
      inputRef.current?.focus()
    }, 200)
    return () => window.clearTimeout(id)
  }, [revealed])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!value || checking) return
    setChecking(true)
    setError(false)
    const ok = await verifyPassword(value)
    setChecking(false)
    if (ok) {
      // Persist for SiteGate / future page loads, but don't let a
      // localStorage failure block a verified unlock. Private-browsing
      // mode, full quota, and disabled storage all throw on setItem;
      // in those cases the user re-types on next visit but at least
      // gets through THIS one. CodeRabbit on PR #241.
      try {
        localStorage.setItem(SITE_PASSWORD_KEY, value)
        // Same-tab signal for components mounted alongside the gate
        // (e.g. ProviderSwitcher — gh#247) that need to know the gate
        // just cleared. Inside the try because listeners typically
        // read from localStorage to attach the X-Site-Password
        // header; firing the event when persistence failed would
        // just produce another 401.
        window.dispatchEvent(new Event('sitegate:unlocked'))
      } catch {
        // Continue with in-memory unlock even if persistence fails.
      } finally {
        onUnlocked()
      }
    } else {
      setError(true)
      setValue('')
      // Re-focus so the user can retry without an extra click.
      inputRef.current?.focus()
    }
  }

  return (
    <>
      {!compact && (
        <>
          <div
            className={s.badge}
            role="status"
            aria-label={TESTING_PANEL_COPY.badge}
          >
            {TESTING_PANEL_COPY.badge}
          </div>
          <p className={s.message}>{TESTING_PANEL_COPY.message}</p>
        </>
      )}

      {!revealed ? (
        <button
          type="button"
          className={s.haveCode}
          onClick={() => setRevealed(true)}
        >
          {TESTING_PANEL_COPY.haveCode}
        </button>
      ) : (
        <form
          className={s.pwForm}
          onSubmit={handleSubmit}
          aria-label={TESTING_PANEL_COPY.haveCode}
        >
          <input
            ref={inputRef}
            type="password"
            aria-label={TESTING_PANEL_COPY.passwordPlaceholder}
            className={[s.pwInput, error && s.pwInputError].filter(Boolean).join(' ')}
            value={value}
            onChange={(e) => {
              setValue(e.target.value)
              if (error) setError(false)
            }}
            placeholder={TESTING_PANEL_COPY.passwordPlaceholder}
            disabled={checking}
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="submit"
            className={s.pwSubmit}
            disabled={!value || checking}
          >
            {checking ? TESTING_PANEL_COPY.checking : TESTING_PANEL_COPY.submit}
          </button>
          {error && (
            <div className={s.pwError} role="alert">
              {TESTING_PANEL_COPY.wrong}
            </div>
          )}
        </form>
      )}
    </>
  )
}
